/**
 * TanStack Query hooks for ReportShareLink resource.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@client/lib/api-client';

export interface ShareLink {
  id: string;
  token: string;
  reportId: string;
  branchId: string;
  expiresAt: string;
  revoked: boolean;
  createdBy: string;
  createdAt: string;
  /** Full public URL — returned by server on creation */
  url?: string;
}

export const shareLinkKeys = {
  all: ['share-links'] as const,
  list: (reportId: string) => ['share-links', 'list', reportId] as const,
};

export function useShareLinks(reportId: string) {
  return useQuery({
    queryKey: shareLinkKeys.list(reportId),
    queryFn: () =>
      api.getPage<ShareLink>(`/api/progress-reports/${reportId}/share-links`),
    enabled: !!reportId,
  });
}

export function useCreateShareLink(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { expiresAt?: string }) =>
      api.post<ShareLink>(`/api/progress-reports/${reportId}/share-links`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: shareLinkKeys.list(reportId) });
    },
  });
}

export function useRevokeShareLink(reportId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      api.patch<ShareLink>(`/api/share-links/${linkId}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: shareLinkKeys.list(reportId) });
    },
  });
}
